/**
 * Navigation Service for FUSE v16 - API Only (No Fallback)
 * Direct 1:1 mapping from backend FuseNavigationItem[] to FUSE components
 * Will fail if API is not available - this is by design
 */

import { FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import { apiClient } from '../client';

/**
 * Navigation Service with TanStack Query integration
 * API-only approach - no fallback navigation
 */
class NavigationService {
	private cachedNavigation: FuseNavItemType[] | null = null;
	private isLoading = false;
	private retryCount = 0;
	private maxRetries = 3;
	private listeners = new Set<() => void>();

	/**
	 * Get navigation items from FUSE-compatible API endpoint
	 * Direct 1:1 mapping - no transformation needed!
	 * Will throw error if API is unavailable
	 */
	async getNavigation(): Promise<FuseNavItemType[]> {
		// Return cached if available
		if (this.cachedNavigation) {
			return this.cachedNavigation;
		}

		// Prevent multiple simultaneous API calls
		if (this.isLoading) {
			while (this.isLoading) {
				await new Promise((resolve) => setTimeout(resolve, 100));
			}

			if (this.cachedNavigation) {
				return this.cachedNavigation;
			}

			throw new Error('Navigation API call failed');
		}

		this.isLoading = true;

		try {
			// Use FUSE-compatible endpoint
			const response = await apiClient.getFuseNavigation();

			if (response.data && Array.isArray(response.data)) {
				this.cachedNavigation = response.data as FuseNavItemType[];
				this.retryCount = 0;
				this.notifyListeners();
				return this.cachedNavigation;
			}

			throw new Error('Invalid navigation data from FUSE API');
		} catch (error) {
			this.retryCount = 0;
			throw error; // Re-throw error instead of using fallback
		} finally {
			this.isLoading = false;
		}
	}

	/**
	 * Get navigation synchronously (for initial render)
	 * Returns cached navigation or empty array - no fallback
	 */
	getNavigationSync(): FuseNavItemType[] {
		return this.cachedNavigation || [];
	}

	/**
	 * Refresh navigation from API (clears cache first)
	 */
	async refreshNavigation(): Promise<FuseNavItemType[]> {
		console.error('🔄 Refreshing navigation...');
		this.cachedNavigation = null;
		this.retryCount = 0;
		const nav = await this.getNavigation();
		this.notifyListeners();
		return nav;
	}

	/**
	 * Clear navigation cache
	 */
	clearCache(): void {
		this.cachedNavigation = null;
		this.retryCount = 0;
		this.notifyListeners();
	}

	/**
	 * Force refresh from API without cache
	 */
	async forceRefresh(): Promise<FuseNavItemType[]> {
		this.clearCache();
		return this.getNavigation();
	}

	/**
	 * Subscribe to navigation changes
	 */
	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	/**
	 * Notify all listeners of navigation changes
	 */
	private notifyListeners(): void {
		this.listeners.forEach((listener) => listener());
	}

	/**
	 * Check if navigation is currently loading
	 */
	getIsLoading(): boolean {
		return this.isLoading;
	}

	/**
	 * Get current retry count (for debugging)
	 */
	getRetryCount(): number {
		return this.retryCount;
	}
}

// Create singleton instance
const navigationService = new NavigationService();

export { navigationService };
export default navigationService;
